import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import SuperAdminSidebar from "../components/SuperAdminSidebar";
import api from "../lib/api";
import { toast } from "sonner";
import { Copy, CheckCircle2, ArrowLeft } from "lucide-react";

export default function SuperAdminNewRestaurant() {
    const navigate = useNavigate();
    const [form, setForm] = useState({
        name: "",
        slug: "",
        owner_email: "",
        owner_username: "",
        owner_password: "",
        phone: "",
        address: "",
        opening_hours: "",
        description: "",
        commission_per_cover_cents: 100,
        bonus_covers_remaining: 0,
    });
    const [loading, setLoading] = useState(false);
    const [created, setCreated] = useState(null);

    const generatePassword = () => {
        const chars = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789";
        let pw = "";
        for (let i = 0; i < 12; i++) pw += chars.charAt(Math.floor(Math.random() * chars.length));
        setForm({ ...form, owner_password: pw + "!" });
    };

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const payload = { ...form };
            if (!payload.slug) delete payload.slug;
            const r = await api.post("/superadmin/restaurants", payload);
            setCreated({ ...r.data, plain_password: form.owner_password });
            toast.success("Ristorante creato con successo");
        } catch (err) {
            toast.error(err.response?.data?.detail || "Errore nella creazione");
        } finally {
            setLoading(false);
        }
    };

    const copy = (text) => {
        navigator.clipboard.writeText(text);
        toast.success("Copiato");
    };

    if (created) {
        return (
            <div className="flex min-h-screen bg-ink">
                <SuperAdminSidebar />
                <main className="flex-1 p-10">
                    <div className="max-w-2xl">
                        <div className="bg-emerald-500/5 border border-emerald-500/30 rounded-sm p-10 text-center mb-6" data-testid="restaurant-created">
                            <CheckCircle2 size={56} className="text-emerald-400 mx-auto mb-4" strokeWidth={1} />
                            <h1 className="font-serif text-4xl text-white">Ristorante creato</h1>
                            <p className="text-gray-400 mt-2">Copia e consegna queste credenziali al ristoratore.</p>
                        </div>

                        <div className="bg-ink-surface border border-gold/30 rounded-sm p-8 space-y-5">
                            <CredRow label="URL del cliente" value={`${window.location.origin}/r/${created.slug}`} onCopy={copy} testid="cred-url" />
                            <CredRow label="Pannello admin" value={`${window.location.origin}/admin/login`} onCopy={copy} testid="cred-admin-url" />
                            <CredRow label="Username" value={created.owner_username} onCopy={copy} testid="cred-username" />
                            <CredRow label="Password" value={created.plain_password} onCopy={copy} testid="cred-password" mono />
                        </div>

                        <div className="flex gap-3 mt-6">
                            <button onClick={() => navigate("/adminrenova/restaurants")}
                                className="bg-gold text-black font-semibold px-6 py-3 rounded-sm hover:bg-gold-light"
                                data-testid="goto-restaurants-btn">
                                Vai ai ristoranti
                            </button>
                            <button onClick={() => { setCreated(null); setForm({ name: "", slug: "", owner_email: "", owner_username: "", owner_password: "", phone: "", address: "", opening_hours: "", description: "", commission_per_cover_cents: 100, bonus_covers_remaining: 0 }); }}
                                className="border border-white/20 text-white px-6 py-3 rounded-sm hover:border-gold hover:text-gold"
                                data-testid="create-another-btn">
                                Creane un altro
                            </button>
                        </div>
                    </div>
                </main>
            </div>
        );
    }

    return (
        <div className="flex min-h-screen bg-ink" data-testid="superadmin-new-restaurant-page">
            <SuperAdminSidebar />
            <main className="flex-1 p-10">
                <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-gray-400 hover:text-gold text-xs uppercase tracking-widest mb-6">
                    <ArrowLeft size={14} /> Indietro
                </button>
                <div className="mb-8">
                    <div className="text-gold text-xs tracking-[0.4em] uppercase">Crea</div>
                    <h1 className="font-serif text-5xl text-white mt-2">Nuovo Ristorante</h1>
                    <p className="text-gray-400 text-sm mt-2">Crea un ristorante e assegna le credenziali al ristoratore. I tavoli e gli slot predefiniti saranno creati automaticamente.</p>
                </div>

                <form onSubmit={submit} className="bg-ink-surface border border-white/10 rounded-sm p-8 max-w-3xl space-y-5">
                    <SectionTitle>Anagrafica ristorante</SectionTitle>
                    <div className="grid sm:grid-cols-2 gap-4">
                        <Field label="Nome ristorante *">
                            <Input value={form.name} onChange={(v) => setForm({ ...form, name: v })} required testid="form-name" placeholder="Es. Osteria del Borgo" />
                        </Field>
                        <Field label="Slug (URL, opzionale)">
                            <Input value={form.slug} onChange={(v) => setForm({ ...form, slug: v })} testid="form-slug" placeholder="es. osteria-del-borgo" />
                        </Field>
                    </div>
                    <div className="grid sm:grid-cols-2 gap-4">
                        <Field label="Telefono">
                            <Input value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} testid="form-phone" placeholder="+39..." />
                        </Field>
                        <Field label="Orari">
                            <Input value={form.opening_hours} onChange={(v) => setForm({ ...form, opening_hours: v })} testid="form-hours" placeholder="Mar-Dom 12:30-14:30 / 19:30-23:00" />
                        </Field>
                    </div>
                    <Field label="Indirizzo">
                        <Input value={form.address} onChange={(v) => setForm({ ...form, address: v })} testid="form-address" />
                    </Field>
                    <Field label="Descrizione">
                        <textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
                            data-testid="form-description"
                            className="w-full px-3 py-2.5 bg-transparent border border-white/20 rounded-sm text-white focus:border-gold focus:outline-none resize-none" />
                    </Field>

                    <SectionTitle>Commissione Renova & bonus coperti</SectionTitle>
                    <div className="grid sm:grid-cols-2 gap-4">
                        <Field label="Commissione per coperto (€1-€5)">
                            <select value={form.commission_per_cover_cents}
                                onChange={(e) => setForm({ ...form, commission_per_cover_cents: parseInt(e.target.value, 10) })}
                                data-testid="form-commission"
                                className="w-full px-3 py-2.5 bg-ink border border-white/20 rounded-sm text-white focus:border-gold focus:outline-none">
                                <option value={100}>€ 1,00 per coperto</option>
                                <option value={200}>€ 2,00 per coperto</option>
                                <option value={300}>€ 3,00 per coperto</option>
                                <option value={400}>€ 4,00 per coperto</option>
                                <option value={500}>€ 5,00 per coperto</option>
                            </select>
                        </Field>
                        <Field label="Coperti bonus gratuiti (decisi da Renova)">
                            <Input type="number" value={String(form.bonus_covers_remaining)}
                                onChange={(v) => setForm({ ...form, bonus_covers_remaining: Math.max(0, parseInt(v || "0", 10)) })}
                                testid="form-bonus-covers" placeholder="0" />
                        </Field>
                    </div>
                    <p className="text-[11px] text-gray-500 -mt-2">I coperti bonus vengono consumati per primi sulle prenotazioni e non generano commissione.</p>

                    <SectionTitle>Credenziali ristoratore</SectionTitle>
                    <Field label="Email owner *">
                        <Input type="email" value={form.owner_email} onChange={(v) => setForm({ ...form, owner_email: v })} required testid="form-owner-email" />
                    </Field>
                    <div className="grid sm:grid-cols-2 gap-4">
                        <Field label="Username *">
                            <Input value={form.owner_username} onChange={(v) => setForm({ ...form, owner_username: v })} required testid="form-owner-username" placeholder="es. mario.rossi" />
                        </Field>
                        <Field label="Password *">
                            <div className="flex gap-2">
                                <Input value={form.owner_password} onChange={(v) => setForm({ ...form, owner_password: v })} required testid="form-owner-password" mono />
                                <button type="button" onClick={generatePassword}
                                    className="text-xs text-gold border border-gold/40 px-3 rounded-sm hover:bg-gold/10 whitespace-nowrap"
                                    data-testid="generate-password-btn">
                                    Genera
                                </button>
                            </div>
                        </Field>
                    </div>

                    <button type="submit" disabled={loading}
                        className="w-full bg-gold text-black font-semibold py-4 rounded-sm hover:bg-gold-light tracking-widest uppercase text-sm disabled:opacity-50"
                        data-testid="create-restaurant-submit-btn">
                        {loading ? "Creazione..." : "Crea ristorante"}
                    </button>
                </form>
            </main>
        </div>
    );
}

function SectionTitle({ children }) {
    return <div className="text-xs uppercase tracking-[0.3em] text-gold border-b border-gold/20 pb-2">{children}</div>;
}
function Field({ label, children }) {
    return <label className="block"><span className="text-[10px] uppercase tracking-[0.3em] text-gray-400 font-semibold">{label}</span><div className="mt-2">{children}</div></label>;
}
function Input({ value, onChange, required, testid, type = "text", placeholder, mono }) {
    return (
        <input type={type} name={testid} value={value} onChange={(e) => onChange(e.target.value)} required={required} placeholder={placeholder}
            data-testid={testid}
            className={`w-full px-3 py-2.5 bg-transparent border border-white/20 rounded-sm text-white placeholder-gray-500 focus:border-gold focus:outline-none focus:ring-1 focus:ring-gold ${mono ? "font-mono" : ""}`} />
    );
}
function CredRow({ label, value, onCopy, testid, mono }) {
    return (
        <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-gray-500 mb-2">{label}</div>
            <div className="flex items-center gap-2">
                <code className={`flex-1 bg-ink border border-white/10 rounded-sm px-3 py-2 text-white text-sm break-all ${mono ? "font-mono" : ""}`} data-testid={testid}>
                    {value}
                </code>
                <button onClick={() => onCopy(value)} className="p-2.5 border border-gold/40 text-gold rounded-sm hover:bg-gold/10" data-testid={`${testid}-copy`}>
                    <Copy size={14} />
                </button>
            </div>
        </div>
    );
}
